'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { gatewayClient } from '@/src/api';
import {
  CashbackProgram,
  CashbackRuleType,
  PublicCashbackRule,
} from '@/src/types/cashback';
import RewardTermsLayout, { RewardTermsSection } from './RewardTermsLayout';
import styles from '../StaticPage.module.css';

// Order must match the `sections` array in the locale JSON, which supplies the
// TOC labels for these entries by index.
const SECTIONS: Omit<RewardTermsSection, 'slot'>[] = [
  { id: 'overview', contentKey: 'overview' },
  { id: 'eligibility', contentKey: 'eligibility' },
  { id: 'calculation', contentKey: 'calculation' },
  { id: 'rates', contentKey: 'rates' },
  { id: 'crediting', contentKey: 'crediting' },
  { id: 'referral-interaction', contentKey: 'referralInteraction' },
  { id: 'using-credit', contentKey: 'usingCredit' },
  { id: 'cancellation', contentKey: 'cancellation' },
  { id: 'fair-use', contentKey: 'fairUse' },
  { id: 'changes', contentKey: 'changes' },
  { id: 'taxes', contentKey: 'taxes' },
  { id: 'contact', contentKey: 'contact' },
];

const formatEur = (cents: number) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

// Rates may be sub-percent (0.33), so don't round to whole numbers.
const formatPercent = (rate: number) =>
  `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(rate)}%`;

const sortRules = (rules: PublicCashbackRule[]): PublicCashbackRule[] =>
  [...rules].sort((a, b) => {
    if (a.ruleType !== b.ruleType) {
      return a.ruleType === CashbackRuleType.LIFETIME_TOTAL ? -1 : 1;
    }
    const threshold = (r: PublicCashbackRule) =>
      r.minLifetimeEurCents ?? r.minOrderEurCents ?? 0;
    return threshold(a) - threshold(b);
  });

const CashbackTerms: React.FC = () => {
  const t = useTranslations('cashbackTerms');

  const [program, setProgram] = useState<CashbackProgram | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    gatewayClient.cashback
      .getProgram()
      .then((data) => {
        if (!cancelled) setProgram(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Rates are admin-tunable at runtime, so they are rendered from the API and
  // never written into the legal copy.
  const ratesSlot = (
    <>
      {failed && (
        <p className={styles.contentText}>{t('ratesTable.unavailable')}</p>
      )}

      {program && program.rules.length === 0 && (
        <p className={styles.contentText}>{t('ratesTable.empty')}</p>
      )}

      {program && program.rules.length > 0 && (
        <table className={styles.dataTable}>
          <caption className={styles.contentText}>
            {t('ratesTable.tiersTitle')}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('ratesTable.conditionLabel')}</th>
              <th scope="col">{t('ratesTable.rateLabel')}</th>
            </tr>
          </thead>
          <tbody>
            {sortRules(program.rules).map((rule, index) => (
              <tr key={`${rule.ruleType}-${index}`}>
                <td>
                  {rule.ruleType === CashbackRuleType.LIFETIME_TOTAL
                    ? `${t('ratesTable.lifetimeLabel')} ${formatEur(rule.minLifetimeEurCents ?? 0)}`
                    : `${t('ratesTable.orderLabel')} ${formatEur(rule.minOrderEurCents ?? 0)}`}
                </td>
                <td>{formatPercent(rule.ratePercent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {program && (
        <p className={styles.contentText}>
          <strong>{t('ratesTable.holdingPeriodLabel')}:</strong>{' '}
          {program.maturityDays} {t('ratesTable.daysLabel')}
        </p>
      )}

      <p className={styles.contentText}>{t('content.rates.note')}</p>
    </>
  );

  const sections: RewardTermsSection[] = SECTIONS.map((section) =>
    section.id === 'rates' ? { ...section, slot: ratesSlot } : section,
  );

  return (
    <RewardTermsLayout
      namespace="cashbackTerms"
      breadcrumbKey="cashbackTerms"
      canonicalPath="/cashback-terms"
      sections={sections}
      related={[
        { labelKey: 'relatedReferral', canonicalPath: '/referral-terms' },
        { labelKey: 'relatedTerms', canonicalPath: '/terms' },
      ]}
    />
  );
};

export default CashbackTerms;
